import { useNavigate } from 'react-router-dom'
import { CreditApplication, Decision, UnderwritingOffer } from '_gql'

import { useGetDecisionsForReviewNavigate } from '@sputnik/pages/gql/GetDecisionsForReviewNavigate.gql'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logInfo } from '@sputnik/utils/loggerUtils'

import useDivvyUUID from './useDivvyUUID'
import useGetCreditAppTypes from './useGetCreditAppTypes'
import useGetIsLoggedInAsAuthSigner from './useGetIsLoggedInAsAuthSigner'

interface UseGoToOffersProps {
  salesforceCreditId: CreditApplication['salesforceCreditId']
  authSignerEmail: string
}

const useGoToOffers = ({ salesforceCreditId, authSignerEmail }: UseGoToOffersProps) => {
  const navigate = useNavigate()
  // a user must be an authorized signer to view the offers
  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({ authSignerEmail })
  const [companyDivvyUuid] = useDivvyUUID(salesforceCreditId)
  const { isAppVersion3, isLoadingCreditAppTypes } = useGetCreditAppTypes({ salesforceCreditId })
  // decisions must be present to view offers
  const { data: decisionsData, loading: isLoadingDecisions } = useGetDecisionsForReviewNavigate({
    skip: !companyDivvyUuid,
    variables: { companyId: companyDivvyUuid },
  })
  const appPath = `/app/${salesforceCreditId}/section/processed`
  const offersPath = `/app/${salesforceCreditId}/${PATHNAME_CONSTS.OFFERS_PATH}`
  const decisions = (decisionsData?.decisions as Decision[]) || []

  const hasSingleDecision = decisions?.length === 1
  const hasMultipleDecisions = (decisions?.length ?? 0) >= 2

  const isLoadingGoToOffers = isLoadingCreditAppTypes || isLoadingDecisions
  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)

  const handleGoToOffers = () => {
    if (hasMultipleDecisions) {
      logInfo('handleGoToOffers', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Navigating to MultiCreditLineOffer page',
        },
        eventName: 'handleGoToOffers',
      })
      isAppVersion3
        ? navigate(`${offersPath}`, { replace: false })
        : navigate(`${appPath}${PATHNAME_CONSTS.MULTI_CREDIT_LINE_OFFER_PATH}`, { replace: false })
    }

    if (hasSingleDecision) {
      logInfo('handleGoToOffers', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Navigating to SingleCreditLineOffer page',
        },
        eventName: 'handleGoToOffers',
      })
      if (isAppVersion3) {
        if (standardLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH}`, { replace: false })
        } else if (variableLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH}`, { replace: false })
        } else if (securedLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.PREPAY_OFFER_PATH}`, {
            replace: false,
          })
        }
      }
      if (!isAppVersion3) navigate(`${appPath}${PATHNAME_CONSTS.SINGLE_CREDIT_LINE_OFFER_PATH}`, { replace: false })
    }
  }

  return { handleGoToOffers, decisions, isLoggedInUserAuthorizedSigner, isLoadingGoToOffers }
}

export default useGoToOffers
